// native
import { Component, OnInit, OnDestroy, Inject, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { Subscription, BehaviorSubject, empty, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';

// service
import { ItemsDisplayService } from '../../../core/services/items-display.service';
import { GlobalsService } from '../../../core/services/globals.service';
import { ItemsWebService } from '../../../core/services/items-web.service';
import { ItemsSyncService } from '../../../core/services/items-sync.service';
import { CitationsEventService } from '../../../core/services/citations-event.service';
import { MatcherService } from '../../../core/services/matcher.service';

// model
import { IDisplayItem, IDataItemQueryParams, ICitationSelection } from '../../../models';

// constants
import { DRAWER_ELEMENT_HEIGHT } from '../../../constants/constants';

@Component({
  selector: 'scs-items',
  templateUrl: './items.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsComponent implements OnInit, OnDestroy {

  @Input() matcherMode: boolean = false;
  @Output() onMatched?: EventEmitter<string> = new EventEmitter();

  paramsSubscription: Subscription;

  citationSelectedSubscription: Subscription;
  itemRemovedFromCitationQueueSubscription: Subscription;

  items: IDisplayItem[] = [];
  itemsParams: IDataItemQueryParams;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingMore$: Subject<boolean> = new Subject<boolean>();

  scrollContainer: HTMLElement;

  constructor(
    public itemsDisplayService: ItemsDisplayService,
    private itemsSyncService: ItemsSyncService,
    public globalsService: GlobalsService,
    private itemsWebService: ItemsWebService,
    private citationsEventService: CitationsEventService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private matcherService: MatcherService
  ) { }

  ngOnInit(): void {
    this.paramsSubscription = this.itemsDisplayService.itemParams$.subscribe((params: IDataItemQueryParams) => {
      this.itemsParams = params;
      this.getItems(params);
    });

    this.initCitationSubscriptions();

    this.scrollContainer = <HTMLElement>this.document.querySelector('#scroll-wrapper-items');
  }

  private initCitationSubscriptions(): void {
    this.citationSelectedSubscription = this.citationsEventService.citationSelected$.subscribe((selection: ICitationSelection) => {
      if (selection.isCitationDeselected) {
        this.uncheckAllItems();
        this.changeDetectorRef.markForCheck();
      }
    });

    this.itemRemovedFromCitationQueueSubscription = this.citationsEventService.itemRemovedFromCitationQueue$.subscribe((removedItem: IDisplayItem) => {
      this.items.forEach((item: IDisplayItem) => {
        if (removedItem.id === item.id) {
          item.isChecked = false;
        }
      });

      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.paramsSubscription?.unsubscribe();
    this.citationSelectedSubscription?.unsubscribe();
    this.itemRemovedFromCitationQueueSubscription?.unsubscribe();
  }

  private getItems(params: IDataItemQueryParams, isScroll = false) {
    this.itemsDisplayService.itemsVisible$.next(true);

    !isScroll && this.itemsDisplayService.itemsLoading$.next(true);
    !isScroll && (this.items = null);
    isScroll && this.loadingMore$.next(true);

    const items$ = params.isGlobalSearch
      ? this.itemsWebService.search(params, isScroll)
      : this.itemsSyncService.getItems(params, isScroll);

    !isScroll && items$.subscribe((items: IDisplayItem[]) => {
      this.itemsDisplayService.itemsLoading$.next(false);

      this.items = items;
    });

    return items$;
  }

  onItemChecked(item: IDisplayItem, event: MouseEvent) {
    item.isChecked = !item.isChecked;

    if (item.isChecked) {
      this.citationsEventService.itemAddedToCitationQueue$.next(item);

      if (event.clientY > (window.innerHeight - 2 * DRAWER_ELEMENT_HEIGHT))
        setTimeout(() => { this.scrollContainer.scrollTop += 2 * DRAWER_ELEMENT_HEIGHT; }, 0);

      return;
    }

    this.citationsEventService.itemUncheckedFromList$.next(item);
  }

  toggleShowAuthors(item: IDisplayItem) {
    item.article.isFullAuthorsShown = !item.article.isFullAuthorsShown;
  }

  onScrollDown() {
    if (this.itemsDisplayService.isNextScrollSearchDisabled) {
      return empty();
    }

    return this.getItems(this.itemsParams, true);
  }

  onScrollPopulateItems(items: IDisplayItem[]) {
    this.items = items;
    this.loadingMore$.next(false);
  }

  private uncheckAllItems() {
    this.items?.length && this.items.forEach((item: IDisplayItem) => {
      item.isChecked = false;
    });
  }

  onMatchClick(item: IDisplayItem) {
    this.onMatched.emit(item.collection_id + ':' + item.id);
  }
}
